import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import * as employeesService from '../../services/api/employees';
import { User } from '../../entities/user';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import { strings } from '../../localization/strings';
import { Pagination } from '../../entities/pagination';
import { SimpleObject } from '../../entities/simple-object';
import FilterUsers from '../../entities/filter/users';
import { fillFromParams } from '../../services/crud/filter';
import Heading1 from '../../components/common/headings/heading1';
import UsersFilter from '../../components/users/filter';
import UsersTableComponent from '../../components/users/table';
import CommonButtonDefault from '../../components/common/buttons/default';

function EmployeesListPageComponent() {
  const [queryParams, setQueryParams] = useSearchParams();

  const initialState: FilterUsers = {
    page: 0,
    sort: '-is_active,name',
  };

  const types = {
    isNumeric: ['page'],
  };

  const { token } = useAuth();
  const [params, setParams] = useState<FilterUsers>(fillFromParams<FilterUsers>(initialState, queryParams, types));
  const [pagination, setPagination] = useState<Pagination>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [employees, setEmployees] = useState<User[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const items = await employeesService.list(token, params);

        setEmployees(items.items);
        setPagination(items.pagination);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [token, params]);

  const setFilterData = (filterData: FilterUsers) => {
    const query: SimpleObject = {};

    for (const [key, value] of Object.entries(filterData)) {
      if (typeof value !== 'undefined' && value !== null && value !== '') {
        query[key] = value;
      }
    }

    setParams({ ...filterData, page: 0 });
    setQueryParams({ ...query, page: 0 } as SimpleObject);
  };

  const updatePage = (currentPage: number) => {
    setParams({ ...params, page: currentPage });
    setQueryParams({ ...params, page: currentPage } as SimpleObject);
  };

  return (
    <div>
      <header className="mb-3">
        <div className="flex justify-between space-x-3">
          <Heading1>{strings.employees.title}</Heading1>
          <Link to="/employees/create">
            <CommonButtonDefault type="button" primary>
              {strings.button.create}
            </CommonButtonDefault>
          </Link>
        </div>
      </header>
      <main className="w-full">
        <div className="overflow-hidden rounded-lg border border-gray-200 sm:rounded-lg">
          {queryParams.get('success') === '1' && (
            <div className="mx-auto my-5 max-w-7xl px-4 sm:px-6 lg:px-8">
              <AlertPrimary size="xs" type="success" text={strings.employees.messages.saved} />
            </div>
          )}
          {error && (
            <div className="mx-auto my-5 max-w-7xl px-4 sm:px-6 lg:px-8">
              <AlertPrimary type="danger" text={error} />
            </div>
          )}
          <UsersFilter onUpdate={setFilterData} initialState={initialState} filterState={params} />
          {isLoading ? (
            <CommonAnimatedLoader />
          ) : (
            <>
              {employees.length > 0 && pagination && (
                <UsersTableComponent
                  users={employees}
                  pagination={pagination}
                  onUpdate={updatePage}
                  editPathBase="/employees/edit"
                  showCompany={false}
                />
              )}
              {employees.length === 0 && (
                <div className="mx-auto max-w-7xl p-4 text-xl sm:p-6 lg:p-8">{strings.search.noResults}</div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default EmployeesListPageComponent;
